import React, { useEffect, useState, useContext, useRef, memo } from "react";
import { Card } from 'primereact/card';
import { Skeleton } from 'primereact/skeleton';
import { useWeb3React } from "@web3-react/core";
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';

import ContractContext from '../../ContractContext'; //Required for all dApp components using contracts

import { formatCurrencyExtended, getTokenPrice, formatTokenIcon, formatDecimals } from '../../utils';
import { miniInfoInvertStyle, miniCardHeaderInvertStyle, infoOutlineInvertStyle } from "./TileStyles";

import BigNumber from 'bignumber.js'

const DashboardTokenInfo = ({ data, getData, refreshBalances }) => {


    const { account } = useWeb3React();
    const contractContext = useContext(ContractContext);
    const contracts = contractContext.contracts;
    const isWalletConnected = React.useCallback(() => account !== null, [account]);

    const LoadingContent = memo(() => <Skeleton width="50%" height="35px" className="mb-2" />);
    const LoadingTitle = memo(() => <Skeleton width="25%" height="25px" className="mb-2" />);

    //states
    const [depositedTokens, setDepositedTokens] = useState();
    const [lastToken, setLastToken] = useState();
    const [pendingRewardTokens, setPendingRewardTokens] = useState([]);
    const [totalDistributedValue, setTotalDistributedValue] = useState(0);

    //refs
    const toast = useRef(null);


    //styles
    const miniCardInvertStyle = (index, row, col) => {
        const baseStyle = {
            background: "#FFFFFF",
            borderRadius: "20px",
            color: "#333333",
            marginBottom: "10px",
            fontFamily: 'Inter, sans-serif',
            transition: '0.3s',
            gridColumn: `span ${col}`,
            gridRow: `span ${row}`,
        };
        return baseStyle;
    };


    //functions
    const getPendingRewards = React.useCallback(async (token = "0x6e61579c22F9a6dA63a33e819f29B6697d2a126E") => {
        const result = await contracts.refuelRewards.methods.getTotalUnrealisedWithRealisedUndeposited(account, token).call();
        //  console.log("getPendingRewards:", result);
        return result;
    }, [contracts.refuelRewards, account]);

    const getTotalTokensDeposited = React.useCallback(async () => {
        return await contracts.refuelDepositer.methods.totalTokensDeposited().call();
    }, [contracts.refuelDepositer]);

    const getLastToken = React.useCallback(async () => {
        return await contracts.refuelDepositer.methods.lastToken().call();
    }, [contracts.refuelDepositer]);

    const getTotalDeposited = React.useCallback(async (token) => {
        return await contracts.refuelDepositer.methods.totalDeposited(token).call();
    }, [contracts.refuelDepositer]);

    //gives a list of all tokens deposited
    const getDepositedTokens = React.useCallback(async () => {
        return await contracts.refuelDepositer.methods.getDepositedTokens().call();
    }, [contracts.refuelDepositer]);


    const getPendingRewardTokens = React.useCallback(async () => {
        const depositedTokens = await getDepositedTokens();
        let _pendingRewardTokens = [];
        //for loop to build array of pending reward tokens to be used for the "Claim" button generated for each token
        for (let i = 0; i < depositedTokens.length; i++) {
            const token = depositedTokens[i];
            const pendingReward = await getPendingRewards(token);
            if (pendingReward > 0) {
                //build a data structure to hold the token address and the pending reward amount
                const pendingRewardToken = {
                    token: token,
                    pendingReward: pendingReward
                }
                //push the data structure into the array
                _pendingRewardTokens.push(pendingRewardToken);
            }
        }
        //console.log("Pending reward tokens: ", _pendingRewardTokens);
        setPendingRewardTokens(_pendingRewardTokens);
    }, [getDepositedTokens, getPendingRewards]);

    const calculateTotalDistributedValue = React.useCallback(async () => {
        const depositedTokens = await getDepositedTokens();
        let _totalDistributedValue = 0;

        for (let i = 0; i < depositedTokens.length; i++) {
            const token = depositedTokens[i];
            const amount = await getTotalDeposited(token);


            const tokenSymbol = data?.tokensList?.find((tokenData) => tokenData.address.toLowerCase() === token.toLowerCase()).label;
            //console.log("Token symbol: ", tokenSymbol);
            const tokenDecimals = data?.tokensList?.find((tokenData) => tokenData.address.toLowerCase() === token.toLowerCase()).decimals;
            //console.log("Token decimals: ", tokenDecimals);
            const tokenPrice = await getTokenPrice(data?.tokensList, tokenSymbol);
            // console.log("Token price: ", tokenPrice);
            _totalDistributedValue += new BigNumber(amount).multipliedBy(tokenPrice).dividedBy(10 ** tokenDecimals).toNumber();
        }
        // console.log("Total distributed value: ", _totalDistributedValue);
        setTotalDistributedValue(_totalDistributedValue);
    }, [getDepositedTokens, getTotalDeposited, data.tokensList]);



    //useEffects
    useEffect(() => {
        if (isWalletConnected() && contracts.refuelRewards) {
            getPendingRewardTokens();
        }
    }, [contracts.refuelRewards, account, data]);
    
    useEffect(() => {
        if (isWalletConnected() && contracts.refuelDepositer) {
            // Fetch deposited tokens
            getTotalTokensDeposited().then(setDepositedTokens);
    
            // Fetch the last token
            getLastToken().then(setLastToken);
        }
    }, [contracts.refuelDepositer, account, data]);
    
    useEffect(() => {
        if (isWalletConnected() && contracts.refuelDepositer && data) {
            calculateTotalDistributedValue();
        }
    }, [account, contracts.refuelDepositer, data]);





    //Web3 Handlers
    const handleClaim = async (token) => {
        //console.log("Refuel Rewards: Claim Rewards button clicked - rocketfiRewards:", rocketfiRewards);
        //console.log("Refuel Rewards: Claim Rewards button clicked - token:", token);

        if (!isWalletConnected()) {
            console.error("Please connect to a wallet before interacting with the contract.");
            return;
        }
        if (!contracts.refuelDepositer) {
            return;
        }
        const refuelDepositer = contracts.refuelDepositer;
        if (!refuelDepositer) {
            console.error("Refuel Rewards contract not available.");
            return;
        }
        if (!token || token === "0x0000000000000000000000000000000000000000") {
            console.error("Token not available.");
            return;
        }

        console.log("Refuel Rewards: Claim Rewards button clicked");

        try {
            const transaction = refuelDepositer.methods.claimDividends(token).send({ from: account });

            transaction.on("transactionHash", (hash) => {
                toast.current.show({ severity: 'info', summary: 'Transaction Submitted', detail: `Transaction Hash: ${hash}. Follow the transaction on BscScan here: https://bscscan.com/tx/${hash}`, life: 3000 });
            });

            const tx = await transaction;

            toast.current.show({ severity: 'success', summary: 'Transaction Success', detail: 'The transaction has been completed successfully.', life: 3000 });


        } catch (error) {
            console.error("Error interacting with the Refuel Rewards contract:", error);
            toast.current.show({ severity: 'error', summary: 'Transaction Error', detail: `Error interacting with the Refuel Rewards contract: ${error.message}`, life: 3000 });
        }
        getData();
        refreshBalances();
    };






    return (
        <Card
            title={
                <div style={infoOutlineInvertStyle}>Summary</div>}
            style={miniCardInvertStyle(0, 1, 1)}

        >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(125px, 1fr))', gap: '5px', marginTop: '10px' }}>
                <div>
                    <div style={miniCardHeaderInvertStyle}>
                        Total Projects Participated
                    </div>
                    <div style={miniInfoInvertStyle}>
                        {depositedTokens
                            ? depositedTokens
                            : <LoadingContent />}
                    </div>
                </div>
                <div>
                    <div style={miniCardHeaderInvertStyle}>
                        Total Distributed Value
                    </div>
                    <div style={miniInfoInvertStyle}>
                        {totalDistributedValue
                            ? formatCurrencyExtended(totalDistributedValue)
                            : <LoadingContent />}
                    </div>
                </div>
                <div>
                    <div style={miniCardHeaderInvertStyle}>
                        Recently Rewarded Project
                    </div>
                    <div style={miniInfoInvertStyle}>
                        {lastToken
                            ? lastToken === "0x0000000000000000000000000000000000000000" ? 'None' : formatTokenIcon(data?.dbData, lastToken)
                            : <LoadingContent />}
                    </div>
                </div>
            </div>
            {pendingRewardTokens?.length > 0 && data.tokensList &&
                <div style={{ marginTop: '25px' }}>
                    Pending Rewards<br />
                    <div style={{ marginTop: '5px', width: '100%', justifyContent: 'center', display: 'flex' }}>
                        {pendingRewardTokens.map((token, index) => {

                            const tokenSymbol = data.tokensList.find((tokenData) => tokenData.address.toLowerCase() === token.token.toLowerCase()).label;
                            const tokenDecimals = data.tokensList.find((tokenData) => tokenData.address.toLowerCase() === token.token.toLowerCase()).decimals;
                            const tokenPrice = getTokenPrice(data.tokensList, tokenSymbol);

                            return (
                                <Button
                                    key={index}
                                    onClick={() => handleClaim(token.token)}
                                    rounded
                                    severity="warning"
                                    size="small"
                                    style={{ width: '100%', justifyContent: 'center', }}
                                >
                                    {formatTokenIcon(data.dbData, token.token)}
                                    {formatDecimals(parseFloat(token.pendingReward), tokenDecimals)}
                                    {' (' + formatCurrencyExtended(new BigNumber(token.pendingReward).dividedBy(10 ** tokenDecimals).multipliedBy(tokenPrice).toNumber()) + ')'}
                                </Button>

                            )
                        }

                        )}
                    </div>

                </div>}
            <Toast ref={toast} />
        </Card>
    );
}

export default DashboardTokenInfo;